import type { PropertyType } from '@/backend/types'
import { getRawValue } from '@/modules/Project/Fields/field-registry'
import type { Field } from '@/modules/Project/Fields/types'
import { wait } from '@/shared/utils/time'
import { useEventListener, whenever } from '@vueuse/core'
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toValue,
  watch,
  type ComponentPublicInstance,
  type MaybeRefOrGetter,
} from 'vue'
import { useRouter, type RouteLocation } from 'vue-router'
/**
 * Helps with the logic of entity sidebar fields that need a click on
 * an "Edit" button to enter edit mode.
 *
 * @returns `isEditing` - Whether the field is in edit mode
 * @returns `latestInputValue` - The latest value of the field that will be submitted on save
 * @returns `fieldHandlers` - Event handlers for the edited field
 * @returns `controlHandlers` - Event handlers for control buttons
 */
export function useExplicitEditMode<T extends PropertyType>(props: {
  field: MaybeRefOrGetter<Field<T>>
  focusable?: MaybeRefOrGetter<ComponentPublicInstance | null>
  onSave: (value: Field<T>['manualValue']) => void
}) {
  const router = useRouter()

  /** Some components hold their own local editing state, and we want to re-render them on reset. */
  const renderKey = ref(0)
  const isEditing = ref(false)
  const showConfirmationDialog = ref(false)

  let detachRouteInterceptor = () => {}
  let interceptedRoute: RouteLocation | undefined = undefined

  const isDirty = computed(() => {
    const field = toValue(props.field)
    const savedValue = getRawValue(field)

    return latestInputValue.value !== undefined && latestInputValue.value !== savedValue
  })

  /** ID for this particular field, we need to reset composable state when it changes */
  const fieldKey = computed(() => {
    const field = toValue(props.field)
    return field.entityId + '-' + field.propertyId
  })

  /**
   * Tracks updated values of the field.
   * Note: `null` is a valid value, `undefined` indicates that the field has not been edited.
   */
  const latestInputValue = ref<Field<T>['manualValue']>()

  const fieldClasses = ref('')

  /** Event handlers to attach to the field being edited */
  const fieldBindings = computed(() => ({
    class: fieldClasses.value,
    key: renderKey.value,
    readonly: !isEditing.value,
    onKeydown: (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        reset()
      }
    },
    onInput: (event: (typeof latestInputValue)['value']) => {
      // Some components might not emit a custom event, but a native one instead.
      if (event instanceof Event) return
      latestInputValue.value = event
    },
    onChange: (event: (typeof latestInputValue)['value']) => {
      // Some components might not emit a custom event, but a native one instead.
      if (event instanceof Event) return
      latestInputValue.value = event
    },
  }))

  /** Event handlers to attach to the control buttons */
  const controlHandlers = reactive({
    onCancel: () => reset(),
    onEdit: () => (isEditing.value = true),
    onSave: () => {
      if (latestInputValue.value !== undefined) {
        props.onSave(latestInputValue.value)
        reset()
      }
    },
  })

  /** Field key changes when we navigate to a different entity. */
  watch(fieldKey, () => reset(), { immediate: true })

  /**
   * When editing gets activated, focus the field.
   */
  whenever(isEditing, async () => {
    const focusable = toValue(props.focusable)

    if (focusable && 'focus' in focusable && typeof focusable.focus === 'function') {
      await wait()
      focusable.focus()
    }
  })

  /**
   * When editing gets activated, coordinate background color so that it
   * appears blue immediately, then fades away over 2 seconds.
   */
  whenever(isEditing, async () => {
    fieldClasses.value = 'bg-background-blue-subtle!'
    await wait()
    fieldClasses.value = 'bg-background-transparent transition duration-[2s]'
  })

  onMounted(() => {
    /** Prevents the user from leaving the page with unsaved changes. */
    detachRouteInterceptor = router.beforeEach((_to, _from, next) => {
      showConfirmationDialog.value = isDirty.value
      // If not dirty, we can navigate immediately, otherwise prevent navigation
      next(!isDirty.value)
      interceptedRoute = isDirty.value ? _to : undefined
    })
  })

  function discardChanges() {
    if (!interceptedRoute) return
    /** Store route temporarily because `reset()` will clear it */
    const route = interceptedRoute
    reset()
    router.push(route)
  }

  onUnmounted(() => detachRouteInterceptor())

  /** Prevents the user from leaving the page with unsaved changes. */
  useEventListener(window, 'beforeunload', (event) => {
    if (!isDirty.value || showConfirmationDialog.value) {
      return
    }
    event.preventDefault()
  })

  function reset() {
    isEditing.value = false
    latestInputValue.value = undefined
    detachRouteInterceptor = () => {}
    interceptedRoute = undefined
    renderKey.value++
  }

  return {
    controlHandlers,
    discardChanges,
    fieldBindings,
    isEditing,
    showConfirmationDialog,
  }
}
